import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { flow, curry } from 'lodash/fp';
import memoize from 'memoize-one';
import { Row } from 'react-flexbox-grid';
import { cities, categories } from './logic.js';
import {
  Section,
  SectionHeader,
  Link,
  Button,
  Icon,
  Paragraph,
  LoadMorePagination,
} from 'components';
import { ICON_TYPES } from 'config';
import { SingleOffer } from '..';
import './Offers.scss';

class Offers extends Component {
  filterOffers = memoize(
    curry((categoryFilter, cityFilter, _data) =>
      _data
        .filter(item => {
          if (categoryFilter !== null) {
            return item.node.frontmatter.settings.category === categoryFilter;
          }
          return item;
        })
        .filter(item => {
          if (cityFilter !== null) {
            return item.node.frontmatter.mainContent.city === cityFilter;
          }
          return item;
        }),
    ),
  );

  constructor(props) {
    super(props);

    this.refCityFilter = createRef();
    this.refCategoryFilter = createRef();

    this.state = {
      cityFilter: null,
      categoryFilter: null,
      isCityActive: false,
      isCategoryActive: false,
    };
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClick, false);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClick, false);
  }

  getCalculatedOffers = () => {
    const { categoryFilter, cityFilter } = this.state;
    const { offers } = this.props;

    const calculateData = flow(this.filterOffers(categoryFilter, cityFilter));

    return calculateData(offers);
  };

  handleClick = e => {
    if (this.refCityFilter.current && this.refCategoryFilter.current) {
      if (
        this.refCityFilter.current.contains(e.target) ||
        this.refCategoryFilter.current.contains(e.target)
      ) {
        return;
      }
      this.setState({ isCategoryActive: false, isCityActive: false });
    }
  };

  renderOffers = offers =>
    offers.map(offer => (
      <SingleOffer key={`offer-${offer.node.id}`} offer={offer} />
    ));

  renderOffersRows = filtered =>
    filtered.length > 0 ? (
      <Row className={'no-gap post-list__row'}>
        {this.renderOffers(filtered)}
      </Row>
    ) : (
      <Paragraph>Offers in this category will appear soon.</Paragraph>
    );

  toggleCityFilterView = () =>
    this.setState(prevState => ({
      isCityActive: !prevState.isCityActive,
      isCategoryActive: false,
    }));

  toggleCategoryFilterView = () =>
    this.setState(prevState => ({
      isCategoryActive: !prevState.isCategoryActive,
      isCityActive: false,
    }));

  updateFilters(label, filter) {
    const keysByLabel = {
      City: 'cityFilter',
      Category: 'categoryFilter',
    };

    this.setState({
      [keysByLabel[label]]: filter,
    });
  }

  checkResults(label, newFilter, navigatePage) {
    navigatePage(1);

    this.updateFilters(label, newFilter);
  }

  renderOffersFilters(label, data, navigatePage) {
    const { cityFilter, categoryFilter } = this.state;

    const filters = data.map(filter => (
      <li
        key={`filter-${filter}`}
        className={`offers__filter-button ${cityFilter === filter ||
          (categoryFilter === filter && 'offers__filter-button--active')}`}
        onClick={() => {
          this.checkResults(label, filter, navigatePage);
        }}
      >
        {filter}
      </li>
    ));

    return (
      <ul className={'offers__filters-list'}>
        <li
          className={'offers__filter-button'}
          onClick={() => {
            this.checkResults(label, null, navigatePage);
          }}
        >
          All
        </li>
        {filters}
      </ul>
    );
  }

  render() {
    const { cityFilter, categoryFilter } = this.state;

    return (
      <Section id={'offers'} additionalClass={'offers'}>
        <SectionHeader size={'small'} headingType={2} lineColor={'yellow'}>
          Job
          <br />
          positions
        </SectionHeader>
        <LoadMorePagination
          index="page"
          loadCount={6}
          data={this.getCalculatedOffers()}
        >
          {({ items, loadMore, navigatePage }) => (
            <>
              <div className={'offers__filters'}>
                <div
                  className={`offers__filter ${this.state.isCityActive &&
                    'opened'}`}
                  onClick={this.toggleCityFilterView}
                  ref={this.refCityFilter}
                >
                  <Paragraph>{cityFilter || 'City'}</Paragraph>
                  <Icon
                    icon={ICON_TYPES.RIGHT_ARROW}
                    size={'default'}
                    color={'black'}
                  />
                  {this.renderOffersFilters('City', cities, navigatePage)}
                </div>
                <div
                  className={`offers__filter ${this.state.isCategoryActive &&
                    'opened'}`}
                  onClick={this.toggleCategoryFilterView}
                  ref={this.refCategoryFilter}
                >
                  <Paragraph>{categoryFilter || 'Category'}</Paragraph>
                  <Icon
                    icon={ICON_TYPES.RIGHT_ARROW}
                    size={'default'}
                    color={'black'}
                  />
                  {this.renderOffersFilters(
                    'Category',
                    categories,
                    navigatePage,
                  )}
                </div>
              </div>
              <div className={'offers__container'}>
                {this.renderOffersRows(items)}
              </div>
              <div className={'offers__button-wrapper'}>
                {loadMore && (
                  <div className={'offers__button'}>
                    <Button
                      tag={Link}
                      to={loadMore.path}
                      transparent
                      color={'black'}
                      onClick={loadMore.action}
                    >
                      Load more
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </LoadMorePagination>
      </Section>
    );
  }
}

Offers.propTypes = {
  offers: PropTypes.array.isRequired,
};

export default Offers;
